import { useState, useEffect, useCallback } from 'preact/hooks';
import type { Socket } from 'socket.io-client';
import { WsEvent } from '@voice-assistant/shared';
import type { VoiceMemoSummary, VoiceMemoStats, VoiceMemoGroupSummary, ProjectPlanSummary } from '@voice-assistant/shared';
import { authFetch } from '../api/authFetch';
import { blobToBase64 } from '../utils/blobToBase64';

interface UseMemoOptions {
  socket: Socket | null;
  connected: boolean;
}

interface UseMemoOptionsExtended extends UseMemoOptions {
  /** Called when a voice bug is transcribed (intent='fix' → caller should sendText; intent='plan' → show toast) */
  onBugTranscribed?: (payload: { id: string; transcript: string; intent: string }) => void;
}

export function useMemos({ socket, connected, onBugTranscribed }: UseMemoOptionsExtended) {
  const [memos, setMemos] = useState<VoiceMemoSummary[]>([]);
  const [groups, setGroups] = useState<VoiceMemoGroupSummary[]>([]);
  const [plans, setPlans] = useState<ProjectPlanSummary[]>([]);
  const [stats, setStats] = useState<VoiceMemoStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastAutoPinEvent, setLastAutoPinEvent] = useState<{ id: string; pendingMemoCount: number; autoReplanThreshold: number; autoReplanTriggered: boolean } | null>(null);

  const fetchMemos = useCallback(async (status?: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      params.set('limit', '100');
      const res = await authFetch(`/api/memos?${params}`);
      if (res.ok) setMemos((await res.json()).memos || []);
    } catch (err) { console.error('Failed to fetch memos:', err); }
    finally { setLoading(false); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos/stats');
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error('Failed to fetch memo stats:', err); }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos/groups/list');
      if (res.ok) setGroups((await res.json()).groups || []);
    } catch (err) { console.error('Failed to fetch groups:', err); }
  }, []);

  // Save a text memo with optional image attachments
  const saveTextMemo = useCallback(async (text: string, images?: { data: string; mimeType: string; fileName: string }[]): Promise<void> => {
    const res = await authFetch('/api/memos/from-text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, ...(images?.length ? { images } : {}) }),
    });
    if (!res.ok) throw new Error(await res.text());
  }, []);

  const saveMemo = useCallback(async (chunks: Blob[], durationMs: number, images?: { data: string; mimeType: string; fileName: string }[], language?: string) => {
    if (!socket || !connected) return;
    const combined = new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' });
    // Send the chunk base64-encoded as a STRING (not a binary attachment) so socket.io
    // emits a plain EVENT packet, never a BINARY_EVENT — eliminating the binary-attachment
    // reassembly desync that corrupted prod audio (#1620 STT fix; #1622 memo-path mirror).
    const chunkB64 = await blobToBase64(combined);
    socket.emit(WsEvent.MEMO_AUDIO_CHUNK, { chunkB64 });
    const payload: any = { durationMs, language: language || 'en' };
    if (images?.length) payload.images = images;
    socket.emit(WsEvent.MEMO_AUDIO_END, payload);
  }, [socket, connected]);

  const transcribeMemo = useCallback(async (id: string) => {
    try {
      const res = await authFetch(`/api/memos/${id}/transcribe`, { method: 'POST' });
      if (res.ok) {
        const updated = await res.json();
        setMemos((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
        return updated;
      }
    } catch (err) { console.error('Failed to transcribe:', err); }
    return null;
  }, []);

  const batchTranscribe = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos/batch-transcribe', { method: 'POST' });
      if (res.ok) { const r = await res.json(); await fetchMemos(); await fetchStats(); return r; }
    } catch (err) { console.error('Batch transcribe failed:', err); }
    return null;
  }, [fetchMemos, fetchStats]);

  // Declared before processMemo to avoid TDZ (processMemo calls fetchPlans in its body)
  const fetchPlans = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos/plans/list');
      if (res.ok) setPlans((await res.json()).plans || []);
    } catch (err) { console.error('Failed to fetch plans:', err); }
  }, []);

  const processMemo = useCallback(async (id: string) => {
    try {
      setMemos((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'processing' } : m)));
      const res = await authFetch(`/api/memos/${id}/add-to-plan`, { method: 'POST' });
      if (res.ok) {
        const result = await res.json();
        setMemos((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'planned' } : m)));
        fetchStats();
        fetchPlans();
        return result;
      }
    } catch (err) {
      console.error('Failed to add to plan:', err);
      setMemos((prev) => prev.map((m) => (m.id === id && m.status === 'processing' ? { ...m, status: 'transcribed' } : m)));
    }
    return null;
  }, [fetchStats]);

  const deleteMemo = useCallback(async (id: string) => {
    try {
      const res = await authFetch(`/api/memos/${id}`, { method: 'DELETE' });
      if (res.ok) { setMemos((prev) => prev.filter((m) => m.id !== id)); fetchStats(); }
    } catch (err) { console.error('Failed to delete:', err); }
  }, [fetchStats]);

  const updateMemo = useCallback(async (id: string, patch: { title?: string; tags?: string[] }) => {
    try {
      const res = await authFetch(`/api/memos/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        setMemos((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
      }
    } catch (err) { console.error('Failed to update:', err); }
  }, []);

  // Re-triage a single memo (calls Ollama again)
  const retriageMemo = useCallback(async (id: string) => {
    try {
      const res = await authFetch(`/api/memos/${id}/triage`, { method: 'POST' });
      if (res.ok) {
        const updated = await res.json();
        setMemos((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
        return updated;
      }
    } catch (err) { console.error('Re-triage failed:', err); }
    return null;
  }, []);

  // ─── Distillation (A/B testing) ─────────────────────────────────

  const distillMemo = useCallback(async (id: string) => {
    try {
      const res = await authFetch(`/api/memos/${id}/distill`, { method: 'POST' });
      if (res.ok) {
        const updated = await res.json();
        setMemos((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
        return updated;
      }
    } catch (err) { console.error('Distill failed:', err); }
    return null;
  }, []);

  const batchDistill = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos/batch-distill', { method: 'POST' });
      if (res.ok) { const r = await res.json(); await fetchMemos(); return r; }
    } catch (err) { console.error('Batch distill failed:', err); }
    return null;
  }, [fetchMemos]);

  // ─── Triage & Groups ────────────────────────────────────────────

  const batchTriage = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos/batch-triage', { method: 'POST' });
      if (res.ok) { const r = await res.json(); await fetchMemos(); return r; }
    } catch (err) { console.error('Batch triage failed:', err); }
    return null;
  }, [fetchMemos]);

  const autoGroup = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos/auto-group', { method: 'POST' });
      if (res.ok) { const r = await res.json(); await fetchGroups(); await fetchMemos(); return r; }
    } catch (err) { console.error('Auto-group failed:', err); }
    return null;
  }, [fetchGroups, fetchMemos]);

  const planGroup = useCallback(async (groupId: string) => {
    try {
      setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, status: 'planning' } : g)));
      const res = await authFetch(`/api/memos/groups/${groupId}/plan`, { method: 'POST' });
      if (res.ok) {
        await fetchGroups();
        await fetchMemos();
        await fetchStats();
        return await res.json();
      }
    } catch (err) {
      console.error('Plan group failed:', err);
      setGroups((prev) => prev.map((g) => (g.id === groupId && g.status === 'planning' ? { ...g, status: 'open' } : g)));
    }
    return null;
  }, [fetchGroups, fetchMemos, fetchStats]);

  /**
   * Plan all open groups sequentially — updates UI after each group completes.
   * Returns progress via onProgress callback so the page can show toasts per group.
   */
  const planAllGroups = useCallback(async (onProgress?: (groupName: string, idx: number, total: number) => void) => {
    const openGroups = groups.filter((g) => g.status === 'open' || g.status === 'stale');
    if (openGroups.length === 0) return { planned: 0, errors: 0 };

    let planned = 0, errors = 0;
    for (let i = 0; i < openGroups.length; i++) {
      const group = openGroups[i];
      onProgress?.(group.name, i + 1, openGroups.length);
      try {
        setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, status: 'planning' } : g)));
        const res = await authFetch(`/api/memos/groups/${group.id}/plan`, { method: 'POST' });
        if (res.ok) {
          planned++;
          // Refresh groups list after each completion so UI updates immediately
          await fetchGroups();
        } else {
          errors++;
          setGroups((prev) => prev.map((g) => (g.id === group.id && g.status === 'planning' ? { ...g, status: 'open' } : g)));
        }
      } catch {
        errors++;
        setGroups((prev) => prev.map((g) => (g.id === group.id && g.status === 'planning' ? { ...g, status: 'open' } : g)));
      }
    }
    await fetchMemos();
    await fetchStats();
    return { planned, errors };
  }, [groups, fetchGroups, fetchMemos, fetchStats]);

  // Promote a group plan to a full Claude session
  const promoteGroup = useCallback(async (groupId: string): Promise<string | null> => {
    try {
      const res = await authFetch(`/api/memos/groups/${groupId}/promote`, { method: 'POST' });
      if (res.ok) {
        const { sessionId } = await res.json();
        await fetchGroups();
        return sessionId;
      }
    } catch (err) { console.error('Promote failed:', err); }
    return null;
  }, [fetchGroups]);

  // Promote a single memo plan to a full Claude session
  const promoteMemo = useCallback(async (memoId: string): Promise<string | null> => {
    try {
      const res = await authFetch(`/api/memos/${memoId}/promote`, { method: 'POST' });
      if (res.ok) {
        const { sessionId } = await res.json();
        return sessionId;
      }
    } catch (err) { console.error('Promote failed:', err); }
    return null;
  }, []);

  const deleteGroup = useCallback(async (groupId: string) => {
    try {
      const res = await authFetch(`/api/memos/groups/${groupId}`, { method: 'DELETE' });
      if (res.ok) { setGroups((prev) => prev.filter((g) => g.id !== groupId)); await fetchMemos(); }
    } catch (err) { console.error('Failed to delete group:', err); }
  }, [fetchMemos]);

  // ─── Living Project Plans ──────────────────────────────────────
  // Note: fetchPlans is declared earlier (before processMemo) to avoid TDZ.

  const generatePlan = useCallback(async (projectKey: string, model?: 'opus' | 'sonnet') => {
    try {
      const res = await authFetch('/api/memos/plans/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectKey, model }),
      });
      if (res.ok) {
        await fetchPlans();
        await fetchStats();
        return await res.json();
      }
    } catch (err) { console.error('Generate plan failed:', err); }
    return null;
  }, [fetchPlans, fetchStats]);

  const deletePlan = useCallback(async (planId: string) => {
    try {
      const res = await authFetch(`/api/memos/plans/${planId}`, { method: 'DELETE' });
      if (res.ok) setPlans((prev) => prev.filter((p) => p.id !== planId));
    } catch (err) { console.error('Failed to delete plan:', err); }
  }, []);

  /** Execute a single chunk via Claude SDK + Stage 1+2 validation */
  const executeChunk = useCallback(async (chunkId: string): Promise<any> => {
    try {
      // Optimistically mark in-progress
      setPlans((prev) => prev.map((p) => ({
        ...p,
        chunks: p.chunks.map((c) => c.id === chunkId ? { ...c, status: 'in-progress' } : c),
      })));
      const res = await authFetch(`/api/memos/plans/chunks/${chunkId}/execute`, {
        method: 'POST',
      });
      if (res.ok) {
        const updated = await res.json();
        // Refresh plans to get latest chunk statuses
        await fetchPlans();
        return updated;
      } else {
        const err = await res.json().catch(() => ({}));
        // Revert optimistic update
        await fetchPlans();
        return { error: err.message || 'Execution failed' };
      }
    } catch (err) {
      console.error('Chunk execution failed:', err);
      await fetchPlans();
      return { error: String(err) };
    }
  }, [fetchPlans]);

  /** Enqueue all pending chunks for a plan */
  const executeAllPending = useCallback(async (planId: string): Promise<number> => {
    try {
      const res = await authFetch(`/api/memos/plans/${planId}/execute-all`, {
        method: 'POST',
      });
      if (res.ok) {
        const { enqueued } = await res.json();
        return enqueued;
      }
    } catch (err) { console.error('Execute all failed:', err); }
    return 0;
  }, []);

  /** Get queue stats + cost */
  const fetchQueueStats = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos/queue/stats');
      if (res.ok) return await res.json();
    } catch (err) { console.error('Failed to fetch queue stats:', err); }
    return null;
  }, []);

  /** Pause the chunk queue */
  const pauseQueue = useCallback(async () => {
    try {
      await authFetch('/api/memos/queue/pause', { method: 'POST' });
    } catch (err) { console.error('Pause failed:', err); }
  }, []);

  /** Resume the chunk queue */
  const resumeQueue = useCallback(async () => {
    try {
      await authFetch('/api/memos/queue/resume', { method: 'POST' });
    } catch (err) { console.error('Resume failed:', err); }
  }, []);

  /** Manually update a chunk's status (approve, defer, etc.) */
  const updateChunkStatus = useCallback(async (chunkId: string, status: string) => {
    try {
      const res = await authFetch(`/api/memos/plans/chunks/${chunkId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        setPlans((prev) => prev.map((p) => ({
          ...p,
          chunks: p.chunks.map((c) => c.id === chunkId ? { ...c, status } : c),
        })));
        return true;
      }
    } catch (err) { console.error('Failed to update chunk status:', err); }
    return false;
  }, []);

  /** Bulk update chunk statuses (skip selected, retry selected, etc.) */
  const bulkUpdateChunkStatus = useCallback(async (chunkIds: string[], status: string) => {
    let ok = 0;
    for (const id of chunkIds) {
      try {
        const res = await authFetch(`/api/memos/plans/chunks/${id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        });
        if (res.ok) ok++;
      } catch {}
    }
    // Optimistic batch update
    setPlans((prev) => prev.map((p) => ({
      ...p,
      chunks: p.chunks.map((c) => chunkIds.includes(c.id) ? { ...c, status } : c),
    })));
    return ok;
  }, []);

  /** Skip all failed chunks and re-execute remaining pending */
  const skipFailuresAndContinue = useCallback(async (planId: string) => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return 0;
    const failedIds = plan.chunks.filter((c) => c.status === 'failed').map((c) => c.id);
    if (failedIds.length > 0) {
      await bulkUpdateChunkStatus(failedIds, 'deferred');
    }
    // Re-enqueue remaining pending
    const pendingCount = plan.chunks.filter((c) => c.status === 'pending').length;
    if (pendingCount > 0) {
      return executeAllPending(planId);
    }
    return 0;
  }, [plans, bulkUpdateChunkStatus, executeAllPending]);

  // ─── Branch Integration ────────────────────────────────────────

  /** Squash-merge plan branch into main */
  const mergePlanBranch = useCallback(async (planId: string): Promise<{ ok: boolean; message: string }> => {
    try {
      const res = await authFetch(`/api/memos/plans/${planId}/merge-branch`, {
        method: 'POST',
      });
      const data = await res.json();
      await fetchPlans();
      return data;
    } catch (err) {
      console.error('Merge failed:', err);
      return { ok: false, message: String(err) };
    }
  }, [fetchPlans]);

  /** Discard plan branch (delete local + remote) */
  const discardPlanBranch = useCallback(async (planId: string): Promise<boolean> => {
    try {
      const res = await authFetch(`/api/memos/plans/${planId}/discard-branch`, {
        method: 'POST',
      });
      if (res.ok) {
        await fetchPlans();
        return true;
      }
    } catch (err) { console.error('Discard failed:', err); }
    return false;
  }, [fetchPlans]);

  /** Resolve merge conflicts via ephemeral Sonnet session */
  const resolveConflicts = useCallback(async (planId: string): Promise<{ ok: boolean; message: string }> => {
    try {
      const res = await authFetch(`/api/memos/plans/${planId}/resolve-conflicts`, {
        method: 'POST',
      });
      const data = await res.json();
      await fetchPlans();
      return data;
    } catch (err) {
      console.error('Resolve conflicts failed:', err);
      return { ok: false, message: String(err) };
    }
  }, [fetchPlans]);

  // ─── WebSocket Events ──────────────────────────────────────────

  useEffect(() => {
    if (!socket) return;
    const handleSaved = (data: any) => {
      const newMemo: VoiceMemoSummary = {
        id: data.id, title: null, status: 'recorded',
        durationMs: data.durationMs || null, audioSizeBytes: data.audioSizeBytes || 0,
        transcript: null, distilledTranscript: null, aiSummary: null, tags: [], imageCount: data.imageCount || 0,
        projectTags: null, groupId: null, errorMessage: null,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.createdAt || new Date().toISOString(),
      };
      setMemos((prev) => [newMemo, ...prev]);
      fetchStats();
    };
    const handleTranscribed = (data: any) => {
      // Auto-transcription completed — update the memo in the list
      setMemos((prev) => prev.map((m) => (m.id === data.id ? {
        ...m, status: data.status, transcript: data.transcript,
        title: data.title, projectTags: data.projectTags,
      } : m)));
      fetchStats();
    };
    const handleError = (data: any) => console.error('Memo error:', data.message);

    // Auto-pin event (settings.autoPin enabled)
    const handleAutoPinned = (data: any) => {
      setMemos((prev) => prev.map((m) => (m.id === data.id ? { ...m, status: 'planned' } : m)));
      setLastAutoPinEvent({ id: data.id, pendingMemoCount: data.pendingMemoCount, autoReplanThreshold: data.autoReplanThreshold, autoReplanTriggered: data.autoReplanTriggered });
      fetchPlans();
      fetchStats();
    };

    // Autonomous pipeline events
    const handleChunkStatus = (data: any) => {
      setPlans((prev) => prev.map((p) => ({
        ...p,
        chunks: p.chunks.map((c) => c.id === data.chunkId ? { ...c, status: data.status, riskLevel: data.riskLevel } : c),
      })));
    };
    const handleBatchComplete = () => { fetchPlans(); };
    const handleCostCap = (data: any) => {
      console.warn(`Daily cost cap reached: $${data.todayCostUsd?.toFixed(2)}/$${data.dailyCapUsd}`);
    };
    const handleQueueState = (data: any) => {
      console.log('Queue state:', data.paused ? 'PAUSED' : 'RUNNING', data.reason || '');
    };

    const handleBugTranscribed = (data: { id: string; transcript: string; intent: string }) => {
      onBugTranscribed?.(data);
    };

    socket.on(WsEvent.MEMO_SAVED, handleSaved);
    socket.on(WsEvent.MEMO_TRANSCRIBED, handleTranscribed);
    socket.on(WsEvent.MEMO_AUTO_PINNED, handleAutoPinned);
    socket.on(WsEvent.MEMO_ERROR, handleError);
    socket.on(WsEvent.CHUNK_STATUS, handleChunkStatus);
    socket.on(WsEvent.BATCH_COMPLETE, handleBatchComplete);
    socket.on(WsEvent.COST_CAP_REACHED, handleCostCap);
    socket.on(WsEvent.QUEUE_STATE, handleQueueState);
    socket.on(WsEvent.BUG_TRANSCRIBED, handleBugTranscribed);
    return () => {
      socket.off(WsEvent.MEMO_SAVED, handleSaved);
      socket.off(WsEvent.MEMO_TRANSCRIBED, handleTranscribed);
      socket.off(WsEvent.MEMO_AUTO_PINNED, handleAutoPinned);
      socket.off(WsEvent.MEMO_ERROR, handleError);
      socket.off(WsEvent.CHUNK_STATUS, handleChunkStatus);
      socket.off(WsEvent.BATCH_COMPLETE, handleBatchComplete);
      socket.off(WsEvent.COST_CAP_REACHED, handleCostCap);
      socket.off(WsEvent.QUEUE_STATE, handleQueueState);
      socket.off(WsEvent.BUG_TRANSCRIBED, handleBugTranscribed);
    };
  }, [socket, fetchStats, fetchPlans, onBugTranscribed]);

  return {
    memos, groups, plans, stats, loading, lastAutoPinEvent,
    fetchMemos, fetchStats, fetchGroups, fetchPlans,
    saveMemo, saveTextMemo, transcribeMemo, processMemo, batchTranscribe,
    deleteMemo, updateMemo, retriageMemo, distillMemo, batchDistill,
    batchTriage, autoGroup, planGroup, planAllGroups, deleteGroup,
    promoteGroup, promoteMemo,
    generatePlan, deletePlan,
    executeChunk, updateChunkStatus, bulkUpdateChunkStatus, skipFailuresAndContinue,
    executeAllPending, fetchQueueStats, pauseQueue, resumeQueue,
    mergePlanBranch, discardPlanBranch, resolveConflicts,
  };
}
